import {
  DeviceIn,
  DevicesCardPropsFragment,
  GroupFragment,
  useDeviceGroupQuery,
  useDeviceGroupsQuery,
  useDevicesListQuery,
  useUpdateDeviceGroupMutation
} from "generated/graphql";
import { uniqBy } from "lodash";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

export type Group = Pick<
  GroupFragment,
  "id" | "name" | "createdAt" | "policies"
> & {
  devices: Array<DevicesCardPropsFragment>;
};

const getDevicesFromMap = (
  devicesMap: Record<string, DevicesCardPropsFragment> | undefined,
  deviceSerials: Array<string>
): Array<DevicesCardPropsFragment> => {
  const devices: Array<DevicesCardPropsFragment> = [];
  deviceSerials.forEach(serial => {
    const device = devicesMap?.[serial.toLowerCase()];
    if (device) {
      devices.push(device);
    }
  });

  return devices;
};

const useDevicesBySerials = (deviceSerials: string[]) => {
  const tenantId = useTenantId();

  // if deviceSerials list is empty, add non-existent serial number so query will return no devices instead of all of them
  if (deviceSerials.length === 0) {
    deviceSerials.push("-1");
  }
  const deviceSerialsFilter = deviceSerials.map(serial => ({
    OR: [
      {
        field: "serialNumber",
        eq: serial.toLowerCase()
      },
      {
        field: "serialNumber",
        eq: serial.toUpperCase()
      }
    ]
  }));
  const { data, fetching, stale } = useDevicesListQuery({
    variables: {
      from: 0,
      limit: 1000,
      filter: {
        AND: [
          {
            field: "tenantId",
            eq: tenantId
          },
          {
            OR: deviceSerialsFilter
          }
        ]
      }
    }
  });
  const devicesMap = data?.devices.reduce((map, device) => {
    const serial = device.serialNumber?.toLowerCase();
    if (serial) {
      map[serial] = device;
    }

    return map;
  }, {} as Record<string, DevicesCardPropsFragment>);

  return { devices: devicesMap, fetching, stale };
};

export const useDeviceGroups = (pauseCondition?: boolean) => {
  const tenantId = useTenantId();
  const {
    data: groupsData,
    fetching: groupsFetching,
    stale: groupsStale,
    executor
  } = useDeviceGroupsQuery({
    variables: {
      tenantId
    },
    pause: pauseCondition || false
  });

  // device groups query returns device serials for each group, those will be used to fetch other device info
  const uniqueSerialNumbers = Array.from(
    groupsData?.getAllGroups.groups?.groups.reduce((serials, group) => {
      group.deviceSerials?.forEach(serial => serials.add(serial));
      return serials;
    }, new Set<string>()) ?? []
  );

  const {
    devices,
    fetching: devicesFetching,
    stale: devicesStale
  } = useDevicesBySerials(Array.from(uniqueSerialNumbers));

  const groups:
    | Array<Group>
    | undefined = groupsData?.getAllGroups.groups?.groups.map(group => ({
    ...group,
    devices: getDevicesFromMap(devices, group.deviceSerials ?? [])
  }));

  const fetching = groupsFetching || devicesFetching;
  const stale = groupsStale || devicesStale;

  return { data: groups, fetching, stale, executor };
};

export const useDeviceGroup = (groupId: string) => {
  const {
    data: groupData,
    fetching: groupFetching,
    stale
  } = useDeviceGroupQuery({
    variables: {
      id: groupId
    }
  });

  const groupDoc = groupData?.readGroup.groupDoc;

  // device group query returns device serials for group, those will be used to fetch other device info
  const { devices, fetching: devicesFetching } = useDevicesBySerials(
    groupDoc?.deviceSerials ?? []
  );

  const fetching = groupFetching || devicesFetching;
  const group: Group | undefined = groupDoc
    ? {
        ...groupDoc,
        devices: getDevicesFromMap(devices, groupDoc.deviceSerials ?? [])
      }
    : undefined;

  return { data: group, fetching, stale };
};

export const useRemoveDevicesFromGroup = () => {
  const [, updateDeviceGroup] = useUpdateDeviceGroupMutation();

  return async (group: Group, serials: string[]) => {
    const updatedDevices: Array<DeviceIn> = uniqBy(
      group.devices
        .filter(
          device =>
            device.serialNumber &&
            !serials.some(
              serial =>
                serial.toLowerCase() === device.serialNumber!.toLowerCase()
            )
        )
        .map(device => ({
          serial: device.serialNumber!,
          deviceModel: device.hardwareModel ?? ""
        })),
      "serial"
    );

    return updateDeviceGroup({
      groupId: group.id,
      devices: { devices: updatedDevices },
      createSWPolicy: false
    });
  };
};

export const useAddDevicesToGroup = () => {
  const [, updateDeviceGroup] = useUpdateDeviceGroupMutation();

  return async (group: Group, devices: DeviceIn[]) => {
    const updatedDevices: Array<DeviceIn> = uniqBy(
      [
        ...(group.devices ?? [])
          .filter(device => !!device.serialNumber)
          .map(device => ({
            serial: device.serialNumber!,
            deviceModel: device.hardwareModel ?? ""
          })),
        ...devices
      ],
      "serial"
    );

    const result = await updateDeviceGroup({
      groupId: group.id,
      devices: { devices: updatedDevices },
      createSWPolicy: false
    });

    return {
      result,
      added: updatedDevices.length - group.devices.length
    };
  };
};
